import { Component, Fragment, type ErrorInfo, type ReactNode } from 'react';
import { Box, Button, Typography } from '@mui/material';
import TextChatPage from '../text-chat/TextChatPage';
import type { LearningTeachingRegionProps } from './TextbookTeachingRegion';

interface ChatTeachingRegionErrorBoundaryProps {
  children: ReactNode;
  onTeachingInteraction?: () => void;
}

interface ChatTeachingRegionErrorBoundaryState {
  hasError: boolean;
  recoveryKey: number;
}

class ChatTeachingRegionErrorBoundary extends Component<
  ChatTeachingRegionErrorBoundaryProps,
  ChatTeachingRegionErrorBoundaryState
> {
  public state: ChatTeachingRegionErrorBoundaryState = { hasError: false, recoveryKey: 0 };

  public static getDerivedStateFromError(): ChatTeachingRegionErrorBoundaryState {
    return { hasError: true, recoveryKey: 0 };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[ChatTeachingRegion] Render crash recovered', { error, componentStack: errorInfo.componentStack });
  }

  private handleRetry = () => {
    this.props.onTeachingInteraction?.();
    this.setState((state) => ({ hasError: false, recoveryKey: state.recoveryKey + 1 }));
  };

  public render() {
    if (!this.state.hasError) {
      return <Fragment key={this.state.recoveryKey}>{this.props.children}</Fragment>;
    }

    return (
      <Box className="learning-workspace-page__teaching-region" role="alert">
        <Typography component="h2" variant="h5">Chat is temporarily unavailable</Typography>
        <Typography color="text.secondary">
          Your learning material remains available while chat recovers.
        </Typography>
        <Button onClick={this.handleRetry} variant="outlined">Retry chat</Button>
      </Box>
    );
  }
}

export function ChatTeachingRegion({
  persona,
  material,
  conversationBoundaryKey,
  conversationSessionId,
  studySessionId,
  allowNewChat,
  onNewChat,
  onTeachingInteraction,
  onChatSessionIdentified,
}: LearningTeachingRegionProps) {
  return (
    <Box
      className="learning-workspace-page__teaching-region learning-workspace-page__teaching-region--chat"
      sx={{ height: '100%', minHeight: 0, overflow: 'hidden' }}
    >
      <ChatTeachingRegionErrorBoundary onTeachingInteraction={onTeachingInteraction}>
        <TextChatPage
          presentation="workspace"
          lockedPersona={persona}
          openingPreamble={material.preambleMarkdown}
          workspaceConversationBoundaryKey={conversationBoundaryKey}
          workspaceChatSessionId={conversationSessionId}
          studySessionId={studySessionId}
          chapterSlug={material.slug}
          allowWorkspaceNewChat={allowNewChat}
          onWorkspaceNewChat={onNewChat}
          onTeachingInteraction={onTeachingInteraction}
          onChatSessionIdentified={onChatSessionIdentified}
        />
      </ChatTeachingRegionErrorBoundary>
    </Box>
  );
}
